// Подписи и словари инженерного блока (портал 73). Только показ, никакой логики записи.
//
// ЗАЧЕМ ЗДЕСЬ ЛЕЖАТ ПОДПИСИ ФОРМ
// В зеркале ядра подписи есть не всегда:
//   • верификации — подписи лежат в самих данных (`__label` у каждого поля), словарь не нужен;
//   • отчёты по заявкам — в данных ТОЛЬКО коды полей (`deadline`, `travel_to`), подписей нет;
//   • акты — в данных только коды строк шаблона (`hexid`, `f1215`), подписи в шаблоне портала.
// Поэтому подписи для отчётов и актов скопированы из портала 73
// (src/job_report_schema.js и src/acts_templates.js) и живут здесь.
//
// ВАЖНО: это КОПИЯ, а не источник правды. Портал 73 мы не меняем (прямое требование
// владельца), но он меняется сам — если инженеры получат новое поле, здесь оно окажется
// без подписи и покажется кодом. Это заметно и не ломает экран: см. подписьПоля().
// Порядок полей тоже берётся отсюда: jsonb в Postgres не хранит порядок ключей исходной формы.

// ─────────── адреса разделов ───────────
// Ровно эти пути нужно добавить маршрутами в App.tsx. Они совпадают с тем, что уже
// прописано в меню (components/nav.ts, группа «Инженерия»): там пункты стоят с
// pending: true — появится маршрут, флаг снимается, и раздел встанет на место.
export const ПУТИ = {
  верификации: '/verifications',
  верификация: (id: string | number) => `/verifications/${id}`,
  отчёты: '/job-reports',
  отчёт: (id: string | number) => `/job-reports/${id}`,
  акты: '/equip-acts',
  акт: (id: string | number) => `/equip-acts/${id}`,
} as const

export const РАЗМЕР_СТРАНИЦЫ = 50   // как PAGE в p73_api.py

// ─────────── статусы ───────────
// Один набор у верификаций, отчётов и (частично) актов — портал использует те же слова.

export const СТАТУСЫ: Record<string, string> = {
  draft: 'Черновик',
  submitted: 'На утверждении',
  approved: 'Утверждён',
  rejected: 'Отклонён',
  done: 'Готов',
  open: 'открыта',
  closed: 'закрыта',
}

/** Класс бейджа из styles.css: won — зелёный, lose — красный, пусто — серый. */
export const видСтатуса = (s?: string | null): '' | 'won' | 'lose' => {
  if (s === 'approved' || s === 'done') return 'won'
  if (s === 'rejected') return 'lose'
  return ''
}

export const названиеСтатуса = (s?: string | null) => (s ? СТАТУСЫ[s] ?? s : '—')

export const ТИПЫ_ВЕРИФИКАЦИИ: Record<string, string> = {
  short: 'Краткая',
  full: 'Полная',
}

/** Значение пункта чек-листа верификации. */
export const ОТМЕТКИ: Record<string, string> = {
  ok: 'норма',
  no: 'замечание',
  na: 'не применимо',
}

// ─────────── шаблоны актов (8 видов) ───────────
// key → короткая метка для фильтра и полное название бланка для карточки.
// Скопировано из 73-portal/src/acts_templates.js (поля key/suffix/name).

export interface ШаблонАкта {
  short: string
  name: string
  /** Сколько единиц оборудования допускает бланк: у УКВ до трёх, у АРБ одна. */
  units: number
}

export const АКТ_ШАБЛОНЫ: Record<string, ШаблонАкта> = {
  vhf: { short: 'УКВ (VHF)', units: 3, name: 'Акт ежегодной комплексной проверки ОВЧ-радиостанций двусторонней связи' },
  sart: { short: 'РЛО (SART)', units: 2, name: 'Акт ежегодной комплексной проверки РЛО' },
  aissart: { short: 'AIS-SART', units: 2, name: 'Акт ежегодной комплексной проверки передатчика АИС' },
  batt: { short: 'АКБ ГМССБ', units: 1, name: 'Акт ежегодной комплексной проверки аккумуляторной батареи ГМССБ' },
  epirb: { short: 'АРБ (EPIRB)', units: 1, name: 'Акт ежегодной комплексной проверки АРБ 406 МГц' },
  sbm_epirb: { short: 'БТО АРБ', units: 1, name: 'Акт Берегового Технического Обслуживания АРБ 406 МГц' },
  ais: { short: 'УАИС (AIS)', units: 1, name: 'Акт ежегодной комплексной проверки оборудования УАИС' },
  r6322: { short: 'ГМССБ 6.3.22.2', units: 1, name: 'Отчёт о проверке судового радиооборудования (форма 6.3.22.2 РС)' },
}

export const названиеШаблона = (t?: string | null) =>
  (t && АКТ_ШАБЛОНЫ[t]?.short) || t || '—'

/** Подписи строк бланка: тип акта → код строки → русская подпись (английская половина опущена). */
export const АКТ_ПОДПИСИ: Record<string, Record<string, string>> = {
  vhf: {
    model: 'Модель, завод-изготовитель',
    serial: 'Серийный номер',
    power: 'Мощность несущей передатчика на всех каналах (Вт)',
    housing: 'Проверка целостности корпуса',
    place: 'Проверка места установки и наличия символа ИМО',
    battery: 'Срок годности элементов питания',
  },
  sart: {
    model: 'Модель РЛО, завод-изготовитель',
    serial: 'Серийный номер',
    marking: 'Наличие и качество маркировки на корпусе',
    radar: 'Наличие меток на экране РЛС',
    battery: 'Срок годности элементов питания',
  },
  aissart: {
    model: 'Модель AIS-SART, завод-изготовитель',
    serial: 'Серийный номер',
    id: 'Заводской идентификатор ID (MMSI)',
    marking: 'Наличие и качество маркировки на корпусе',
    target: 'Отображение цели AIS-SART',
    battery: 'Срок годности элементов питания',
  },
  batt: {
    model: 'Модель батареи (завод-изготовитель)',
    year: 'Год установки АКБ',
    voltage: 'Рабочее напряжение, В',
    hours: 'Время работы оборудования ГМССБ (час)',
    rated: 'Номинальная ёмкость аккумулятора (А/ч)',
    actual: 'Остаточная ёмкость аккумулятора (А/ч)',
    remain: 'Остаточная ёмкость батарей составляет, %',
  },
  epirb: {
    model: 'Модель АРБ, завод-изготовитель',
    serial: 'Заводской номер АРБ',
    mcc: 'Наличие документа о регистрации в МКВЦ',
    protocol: 'Тип протокола',
    country: 'Код страны',
    hexid: 'Идентификационный номер (посылка с 26 по 85 бит)',
    msg112: 'Содержание посылки с 1 по 112 бит',
    aisid: 'Идентификатор передатчика АИС (если применимо)',
    bch: 'BCH',
    f1215: 'Наличие несущей частоты передатчика 121,5 МГц',
    f406: 'Измерение несущей частоты 406 МГц',
    strobe: 'Состояние проблескового маяка',
    hru: 'Тип и срок годности устройства автоматического отделения',
    battery_next: 'Дата следующей замены элементов питания',
    sbm_next: 'Дата очередного берегового обслуживания',
    tester: 'Тип и сер. номер измерительного оборудования',
  },
  sbm_epirb: {
    model: 'Модель АРБ, завод-изготовитель',
    serial: 'Заводской номер АРБ',
    country: 'Код страны',
    hexid: 'Идентификационный номер (посылка с 26 по 85 бит)',
    aisid: 'Идентификатор передатчика АИС (если применимо)',
    msg112: 'Содержание посылки с 1 по 112 бит',
    f406: 'Несущая частота 406 МГц и уход частоты',
    f1215: 'Наличие сигнала 121,5 МГц с качанием тона',
    power: 'Выходная мощность передатчика, Вт',
    waterproof: 'Водонепроницаемость',
    strobe: 'Состояние проблескового маяка',
    hru: 'Наличие и срок годности устройства автоматического отделения',
    marking: 'Наличие и качество маркировки на корпусе',
    battery_next: 'Дата следующей замены элементов питания',
    sbm_next: 'Дата очередного БТО',
  },
  ais: {
    // Бланк УАИС разбит на шесть разделов; код строки несёт номер раздела (s1_… — s6_…).
    s1_1: '1.1 Тип аппаратуры АИС (s/n)',
    s1_2: '1.2 Свидетельство о типовом одобрении',
    s1_3: '1.3 Отчёт о первоначальной установке на борту',
    s1_4: '1.4 Наличие чертежей (блоки, антенны, подключения)',
    s1_5: '1.5 Основной источник электроэнергии',
    s1_6: '1.6 Аварийный источник электроэнергии',
    s1_7: '1.7 Проверена ёмкость, если АИС подключена к АКБ',
    s1_8: '1.8 Лоцманский разъём вблизи рабочего места лоцмана',
    s1_9: '1.9 Доступ к 120 В вблизи лоцманского разъёма',
    s2_1: '2.1 MMSI',
    s2_2: '2.2 Номер ИМО',
    s2_3: '2.3 Радиопозывной',
    s2_4: '2.4 Название судна',
    s2_5: '2.5 Тип судна',
    s2_6: '2.6 Длина/ширина судна, м',
    s2_7: '2.7 Расположение GPS антенны (A/B/C/D)',
    s3_1: '3.1 Позиция судна с точностью и целостностью',
    s3_2: '3.2 Время UTC',
    s3_3: '3.3 Курс относительно грунта (COG)',
    s3_4: '3.4 Скорость относительно грунта (SOG)',
    s3_5: '3.5 Курс (гирокомпас или прочее)',
    s3_6: '3.6 Навигационный статус',
    s3_7: '3.7 Угловая скорость поворота (ROT)',
    s4_1: '4.1 Осадка судна',
    s4_2: '4.2 Тип груза',
    s4_3: '4.3 Пункт назначения и ETA',
    s4_4: '4.4 План маршрута (опционально)',
    s4_5: '4.5 Короткие сообщения безопасности',
    s5_1: '5.1 Измерение частоты АИС кан. 1, 2, 70 ЦИВ',
    s5_2: '5.2 Измерение мощности АИС кан. 1, 2, 70 ЦИВ',
    s5_3: '5.3 Информация с опросом на 70 канале',
    s5_4: '5.4 Считывание данных с дисплея АИС',
    s5_5: '5.5 Передача информации в аппаратуру АИС',
    s5_6: '5.6 Проверка АИС на опрос «виртуальных судов»',
    s6_1: '6.1 Проверка функции приёма сигналов',
    s6_2: '6.2 Приём собственного сигнала от других судов / СУДС',
    s6_3: '6.3 Опрос СУДС / береговой станции',
    emi: 'Электромагнитные помехи от АИС другим установкам?',
    remarks: 'Замечания',
    place: 'Место проведения',
  },
  r6322: {
    place: 'Место проведения проверки',
    surveyor: 'Проверку выполнил',
    notes: 'Примечания / замечания',
  },
}

/** Подписи шапки бланка (одинаковы у всех типов, у УАИС шире). */
export const АКТ_ШАПКА: Record<string, string> = {
  vessel: 'Название судна',
  imo: 'Номер ИМО',
  mmsi: 'MMSI',
  call_sign: 'Позывной',
  port_registry: 'Порт приписки',
  gross: 'Валовая вместимость',
  keel: 'Дата закладки киля',
}

/**
 * Пункты чек-листа АРБ (единственный тип акта с чек-листом в шаблоне портала).
 * В данных лежит только номер пункта и отметка: {"13": "no"} — то есть «по пункту 13
 * замечание». Без этого списка на экране был бы бессмысленный «п. 13 — no».
 */
export const АКТ_ПРОВЕРКИ_ARB: string[] = [
  'АРБ расположен и закреплён в месте, обеспечивающем свободное всплытие',
  'Визуальный осмотр АРБ и бракеты (дефекты, повреждения, трещины корпуса)',
  'Самопроверка АРБ, включая самопроверку ГНСС, если применимо',
  'Идентификационный номер АРБ чётко обозначен на внешней стороне',
  'Идентификатор АРБ идентичен информации, нанесённой на корпус',
  'Номер MMSI или позывной соответствует присвоенному судну',
  'Декодированный децимальный номер (MMSI) соответствует судовому MMSI',
  'Проверка регистрации в соответствующей базе данных',
  'Батарея имеет не истекший срок годности',
  'Устройство автоматического отделения имеет не истекший срок годности',
  'Проверка излучения в полосе 406 МГц',
  'Проверка излучения на частоте 121,5 МГц, если применимо',
  'Проверка излучения на частотах АИС, если применимо',
  'АРБ обслуживается с интервалами, требуемыми Администрацией (MSC/Circ.1039)',
  'Линь в хорошем состоянии и надёжно закреплён',
  'После испытаний АРБ на штатном месте, ложные сигналы отсутствуют',
  'Руководство по эксплуатации АРБ имеется на борту',
]

export const подписьСтрокиАкта = (тип: string, код: string) =>
  АКТ_ПОДПИСИ[тип]?.[код] ?? АКТ_ШАПКА[код] ?? код

// ─────────── этапы отчёта по заявке ───────────
// Копия JOB_REPORT_STAGES из 73-portal/src/job_report_schema.js: подписи и ПОРЯДОК.
// В данных ядра ни того, ни другого нет — только коды полей.

export interface ЭтапСхемы {
  code: string
  title: string
  repeat: boolean
  items: Array<{ key: string; label: string; long?: boolean }>
}

export const ЭТАПЫ_ОТЧЁТА: ЭтапСхемы[] = [
  {
    code: 'stage1', title: '1. Подготовка к заявке', repeat: false,
    items: [
      { key: 'app_number', label: 'Номер заявки' },
      { key: 'deadline', label: 'Срок выполнения' },
      { key: 'work_port', label: 'Порт проведения работ' },
      { key: 'app_content', label: 'Содержание заявки', long: true },
      { key: 'doc_status', label: 'Проектная документация' },
    ],
  },
  {
    code: 'stage1_engineers', title: '1.1 Инженерный / монтажный состав', repeat: true,
    items: [
      { key: 'name', label: 'Ф.И.О.' },
      { key: 'role', label: 'Роль' },
    ],
  },
  {
    code: 'stage1_materials', title: '1.2 Материалы и оборудование со склада', repeat: true,
    items: [
      { key: 'name', label: 'Наименование' },
      { key: 'sn', label: 'S/N' },
      { key: 'qty', label: 'Количество' },
      { key: 'notes', label: 'Примечания' },
    ],
  },
  {
    code: 'stage1_problems', title: '1.3 Проблемы со складом', repeat: true,
    items: [{ key: 'description', label: 'Описание проблемы', long: true }],
  },
  {
    code: 'stage2_segments', title: '2. Оценка обстановки на месте', repeat: true,
    items: [
      { key: 'title', label: 'Что снимаем' },
      { key: 'comments', label: 'Комментарии к фото / проблемам', long: true },
    ],
  },
  {
    code: 'stage3_days', title: '3. Ход работ (по дням)', repeat: true,
    items: [
      { key: 'date', label: 'Дата' },
      { key: 'start_time', label: 'Время начала работ' },
      { key: 'end_time', label: 'Время окончания работ' },
      { key: 'travel_to', label: 'Время дороги ТУДА (часы)' },
      { key: 'travel_from', label: 'Время дороги ОБРАТНО (часы)' },
      { key: 'downtime', label: 'Простой (время и причина)', long: true },
      { key: 'comments', label: 'Что сделали за день', long: true },
    ],
  },
  {
    code: 'final', title: '4. Итог', repeat: false,
    items: [
      { key: 'works_done', label: 'Какие работы выполнены', long: true },
      { key: 'works_pending', label: 'Что осталось / на следующий выезд', long: true },
      { key: 'customer_notes', label: 'Замечания заказчика', long: true },
      { key: 'ready_for_act', label: 'Готов к подписанию акта' },
    ],
  },
]

export const схемаЭтапа = (code: string) => ЭТАПЫ_ОТЧЁТА.find((э) => э.code === code)

/** Подпись поля этапа. Нет в схеме — показываем код: значит форма портала ушла вперёд. */
export const подписьПоля = (code: string, key: string) =>
  схемаЭтапа(code)?.items.find((i) => i.key === key)?.label ?? key

export const долгоеПоле = (code: string, key: string) =>
  !!схемаЭтапа(code)?.items.find((i) => i.key === key)?.long

/**
 * Упорядочить значения этапа по схеме портала и добить пустыми полями, которых нет в data:
 * «поля нет» и «поле пустое» для читателя одно и то же, а провал в середине формы —
 * повод думать, что мы что-то потеряли.
 */
export function поляЭтапа(
  code: string,
  items: Array<{ key: string; value: unknown }> | undefined,
): Array<{ key: string; label: string; value: unknown; long: boolean; unknown: boolean }> {
  const было = new Map((items ?? []).map((i) => [i.key, i.value]))
  const схема = схемаЭтапа(code)
  const вышло: Array<{ key: string; label: string; value: unknown; long: boolean; unknown: boolean }> = []
  for (const п of схема?.items ?? []) {
    вышло.push({ key: п.key, label: п.label, value: было.get(п.key) ?? '', long: !!п.long, unknown: false })
    было.delete(п.key)
  }
  // Осталось в данных, но нет в схеме — форма портала изменилась. Показываем кодом.
  for (const [key, value] of было) {
    вышло.push({ key, label: key, value, long: false, unknown: true })
  }
  return вышло
}

// ─────────── показ значений ───────────

/** Значение поля формы: true/false у бланков означает «Годное / Не годное». */
export function значениеФормы(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (v === true) return 'Годное'
  if (v === false) return 'Не годное'
  if (Array.isArray(v)) return v.map((x) => значениеФормы(x)).join(' · ')
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** Заполнено ли поле — для подсчёта «сколько реально внесли». */
export const заполнено = (v: unknown) =>
  !(v === null || v === undefined || v === '' || (Array.isArray(v) && v.every((x) => !x)))

/** Размер файла из реестра фото: в ядре только байты. */
export function размерФайла(b?: number | null): string {
  if (!b) return '—'
  if (b < 1024) return `${b} Б`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} КБ`
  return `${(b / 1024 / 1024).toFixed(1)} МБ`
}

export const процент = (v?: number | null) => (v === null || v === undefined ? '—' : `${v}%`)
